import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Book, ArrowLeft, Camera, Check } from '@phosphor-icons/react';
import { toast } from 'sonner';
import GlobalHeader from '../components/GlobalHeader';

export const Settings = () => {
  const { user, setUser } = useAuth();
  const [bio, setBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const photoInputRef = useRef();
  const coverInputRef = useRef();

  useEffect(() => {
    if (user) {
      setBio(user.bio || '');
    }
  }, [user]);

  const handleUpload = async (file, field) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      if (field === 'profile_photo') setUploadingPhoto(true); else setUploadingCover(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/upload/image`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      const imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.url}`;
      
      const updateRes = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/profile`,
        { [field]: imageUrl },
        { withCredentials: true }
      );
      setUser(updateRes.data);
      toast.success('Image updated');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploadingPhoto(false);
      setUploadingCover(false);
    }
  };

  const handleSaveBio = async () => {
    try {
      setSaving(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/me/profile`,
        { bio },
        { withCredentials: true }
      );
      setUser(res.data);
      toast.success('Profile updated');
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      <GlobalHeader />

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl tracking-tight mb-8" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 700 }}>
            Profile Settings
          </h2>

          <div className="mb-6">
            <label className="block text-sm font-semibold mb-3 uppercase tracking-widest text-muted-foreground">Cover Image</label>
            <div 
              onClick={() => coverInputRef.current?.click()}
              className="aspect-[4/1] bg-muted rounded-lg overflow-hidden relative cursor-pointer group"
              data-testid="cover-image-upload"
            >
              {user.cover_image ? (
                <img src={user.cover_image} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Camera size={48} weight="duotone" className="text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <p className="text-white font-semibold">{uploadingCover ? 'Uploading...' : 'Click to change'}</p>
              </div>
              <input
                ref={coverInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'cover_image')}
              />
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold mb-3 uppercase tracking-widest text-muted-foreground">Profile Photo</label>
            <div className="flex items-center gap-4">
              <div 
                onClick={() => photoInputRef.current?.click()}
                className="w-24 h-24 rounded-full overflow-hidden relative cursor-pointer group border-2 border-border"
                data-testid="profile-photo-upload"
              >
                <img
                  src={user.picture || `https://ui-avatars.com/api/?name=${user.name}`}
                  alt={user.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Camera size={24} className="text-white" />
                </div>
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handleUpload(e.target.files[0], 'profile_photo')}
                />
              </div>
              <div>
                <p className="font-semibold">{user.name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
                {uploadingPhoto && <p className="text-xs text-primary mt-1">Uploading...</p>}
              </div>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-semibold mb-3 uppercase tracking-widest text-muted-foreground">Bio</label>
            <textarea
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell readers about yourself..."
              rows={5}
              maxLength={500}
              data-testid="bio-textarea"
              className="w-full px-4 py-3 bg-card border border-border rounded-lg outline-none focus:border-primary resize-none"
            />
            <p className="text-xs text-muted-foreground mt-1">{bio.length}/500</p>
          </div>

          <button
            onClick={handleSaveBio}
            disabled={saving}
            data-testid="save-button"
            className="bg-primary text-primary-foreground rounded-full py-3 px-8 font-semibold transition-transform hover:scale-[1.02] hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
          >
            <Check size={20} weight="bold" />
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;
